import { useCallback, useEffect } from "react";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { useBlocker, BlockerFunction } from "react-router-dom";
export interface RVReactRouterDirtyPromptProps {
    /**
     * Whether to block navigation.
     */
    whenDirty: boolean;
    /**
     * Message to display when navigation is blocked.
     */
    message?: string;
    /**
     * Function to determine if navigation should be allowed for a given location.
     */
    allowCheck?(location: { pathname: string; search?: string; hash?: string }): boolean;
    /**
     * When false, in-app navigation is never blocked (no modal shown), but browser
     * refresh / tab-close is still gated via beforeunload.
     * Defaults to true.
     */
    blockNavigation?: boolean;
}

/**
 * RVReactRouterDirtyPrompt
 *
 * Handles unsaved changes in React Router v6 by blocking navigation and browser reloads when dirty.
 *
 * @param {boolean} whenDirty - Whether to block navigation.
 * @param {string} [message] - Message to display when navigation is blocked.
 * @param {(location: { pathname: string; search?: string; hash?: string }) => boolean} [allowCheck] - Function to determine if navigation should be allowed for a given location.
 * @returns {null}
 */
export function RVReactRouterDirtyPrompt({
    whenDirty,
    allowCheck = () => false,
    message = "You have unsaved changes. Leave anyway?",
    blockNavigation = true,
}: RVReactRouterDirtyPromptProps) {
    // Determines if navigation should be blocked (confirmation modal shown)
    const blockCheck = useCallback<BlockerFunction>(
        ({ nextLocation }) => {
            if (!blockNavigation || !whenDirty) {
                // only block when state is dirty and blockNavigation is enabled
                return false;
            }
            if (!allowCheck || !allowCheck(nextLocation)) {
                return true;
            }
            return false;
        },
        [allowCheck, blockNavigation, whenDirty]
    );

    const blocker = useBlocker(blockCheck);

    // Handle browser reloads / tab closes
    useEffect(() => {
        if (!whenDirty) {
            return;
        }
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [whenDirty]);

    // whenever navigation becomes blocked, confirm with the user
    useEffect(() => {
        if (blocker.state === "blocked") {
            const confirm = Modal.confirm({
                icon: <ExclamationCircleOutlined />,
                content: message,
                onOk() {
                    blocker.proceed();
                },
                onCancel() {
                    blocker.reset();
                },
            });
            return confirm.destroy;
        }
    }, [blocker, message]);

    return null;
}
